import { createFileRoute, Link } from "@tanstack/react-router";
import ArrowLeftIcon from "lucide-react/dist/esm/icons/arrow-left";
import LoaderCircleIcon from "lucide-react/dist/esm/icons/loader-circle";
import { useId, useMemo } from "react";
import { z } from "zod";

import { BarList } from "@/components/chart/bar-list";
import { LineChart } from "@/components/chart/line-chart";
import { StatTile } from "@/components/metric/stat-tile";
import { PageBody, Panel } from "@/components/shell/page-frame";
import { EmptyState } from "@/components/states/state-views";
import { StatusBadge } from "@/components/status/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { METRICS, type MetricKey } from "@/features/analytics/mock-data";
import {
  getReportMetricSnapshot,
  REPORT_COMPARISON_LABEL,
  REPORT_TYPE_LABEL,
  type Report,
  useReport
} from "@/features/analytics/reports";
import {
  formatCompact,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDelta,
  formatPercent
} from "@/lib/format";
import { cn } from "@/lib/utils";

const reportSearchSchema = z.object({
  metric: z
    .enum(["revenue", "deliveries", "openRate", "conversionRate", "unsubscribeRate"])
    .catch("revenue")
    .default("revenue"),
  compare: z.coerce.boolean().catch(true).default(true)
});

export const Route = createFileRoute("/analytics/$reportId")({
  validateSearch: reportSearchSchema,
  component: ReportDetailPage
});

/**
 * レポート詳細。対象期間を固定したうえで KPI・トレンド・内訳を読む分析系の画面。
 * 期間を動かして探索する用途は持たせない（それは一覧に戻って別レポートを開く）。
 */
function ReportDetailPage() {
  const { reportId } = Route.useParams();
  const report = useReport(reportId);

  if (!report) {
    return (
      <PageBody density="roomy">
        <EmptyState
          title="レポートが見つかりません"
          description="削除されたか、URL が誤っている可能性があります。一覧から選び直してください。"
          action={
            <Button size="sm" variant="outline" render={<Link to="/analytics" />}>
              レポート一覧へ戻る
            </Button>
          }
        />
      </PageBody>
    );
  }

  return <ReportDetailView report={report} />;
}

function ReportDetailView({ report }: { report: Report }) {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const compareId = useId();

  const update = (patch: Partial<typeof search>) => {
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });
  };

  const snapshots = useMemo(
    () =>
      METRICS.map((metric) => ({
        definition: metric,
        snapshot: getReportMetricSnapshot(report, metric.key)
      })),
    [report]
  );

  const active = snapshots.find((entry) => entry.definition.key === search.metric) ?? snapshots[0];
  if (!active) return null;

  const comparisonLabel = REPORT_COMPARISON_LABEL[report.type];
  const periodLabel = `${formatDate(report.periodStart)}〜${formatDate(report.periodEnd)}`;

  return (
    <>
      {/* 詳細専用ヘッダー。一覧への戻り口を持つため PageHeader とは別に組む。 */}
      <div className="sticky top-(--topbar-h) z-30 flex min-h-(--pagehead-h) flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-4 py-2.5">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="レポート一覧へ戻る"
          render={<Link to="/analytics" />}
        >
          <ArrowLeftIcon aria-hidden="true" />
        </Button>
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-lg font-semibold tracking-tight text-fg">{report.title}</h1>
          <Badge variant={report.type === "weekly" ? "secondary" : "outline"}>
            {REPORT_TYPE_LABEL[report.type]}
          </Badge>
          {report.status === "generating" ? <StatusBadge tone="info">生成中</StatusBadge> : null}
        </div>
        <span className="hidden text-xs text-fg-subtle lg:inline">
          対象期間 {periodLabel} · 作成 {formatDateTime(report.createdAt)}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <label htmlFor={compareId} className="flex items-center gap-1.5 text-xs text-fg-muted">
            <Switch
              id={compareId}
              size="sm"
              checked={search.compare}
              onCheckedChange={(checked) => update({ compare: checked })}
            />
            {comparisonLabel}を重ねる
          </label>
        </div>
      </div>

      {report.status === "generating" ? (
        <PageBody density="roomy">
          <Panel title="レポートを生成中" description={`対象期間 ${periodLabel}`}>
            <div
              role="status"
              className="flex items-center justify-center gap-2 py-16 text-sm text-fg-muted"
            >
              <LoaderCircleIcon className="size-4 animate-spin" aria-hidden="true" />
              期間中のKPIを集計し、要約を生成しています…
            </div>
          </Panel>
        </PageBody>
      ) : (
        <PageBody density="roomy" className="grid gap-6">
          {/* 結論から読む。数値の羅列より先に、何が起きて何をすべきかを置く（設計 4-5）。 */}
          <Panel
            title="AI要約"
            description={`${comparisonLabel}での変化要因と、優先度順の推奨アクション`}
          >
            <p className="text-sm leading-relaxed text-fg">{report.summary}</p>
            <ol className="mt-3 grid gap-1.5">
              {report.recommendedActions.map((action, index) => (
                <li key={action} className="flex items-start gap-2 text-sm text-fg">
                  <span className="num mt-0.5 grid size-4 shrink-0 place-items-center rounded-sm bg-surface-sunken text-2xs font-semibold text-fg-muted">
                    {index + 1}
                  </span>
                  {action}
                </li>
              ))}
            </ol>
          </Panel>

          <section aria-label="主要指標" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {snapshots.map(({ definition, snapshot }) => {
              const selected = definition.key === search.metric;
              return (
                <button
                  key={definition.key}
                  type="button"
                  onClick={() => update({ metric: definition.key as MetricKey })}
                  aria-pressed={selected}
                  className={cn(
                    "rounded-lg text-left transition-shadow focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                    selected && "ring-2 ring-brand"
                  )}
                >
                  <StatTile
                    label={definition.label}
                    value={definition.format(snapshot.total)}
                    delta={{
                      value: snapshot.deltaPercent,
                      polarity: definition.polarity,
                      comparisonLabel
                    }}
                    trend={snapshot.current}
                    footnote={snapshot.footnote}
                    emphasis={definition.key === "revenue"}
                    className="h-full"
                  />
                </button>
              );
            })}
          </section>

          <Panel
            title={`${active.definition.label}の推移`}
            description={`対象期間 ${periodLabel}${search.compare ? ` · ${comparisonLabel}を破線で重ねて表示` : ""}`}
          >
            <LineChart
              labels={active.snapshot.labels}
              height={280}
              formatValue={active.definition.format}
              series={[
                {
                  key: "current",
                  label: "対象期間",
                  color: "var(--chart-1)",
                  values: active.snapshot.current
                },
                ...(search.compare
                  ? [
                      {
                        key: "previous",
                        label: comparisonLabel.replace("比", ""),
                        color: "var(--chart-5)",
                        values: active.snapshot.previous,
                        dashed: true
                      }
                    ]
                  : [])
              ]}
            />
          </Panel>

          {/* 全シナリオのKPI横断（設計 4-5）。内訳の合計は主要指標の帰属売上と一致する。 */}
          <Panel
            title="シナリオ別パフォーマンス"
            count={report.scenarioPerformance.length}
            description="帰属売上の内訳と前期比。クリックで該当シナリオへ"
            flush
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
                  <th scope="col" className="px-4 py-2 text-left">
                    シナリオ
                  </th>
                  <th scope="col" className="px-4 py-2 text-right">
                    帰属売上
                  </th>
                  <th scope="col" className="hidden px-4 py-2 text-right sm:table-cell">
                    構成比
                  </th>
                  <th scope="col" className="px-4 py-2 text-right">
                    {comparisonLabel}
                  </th>
                  <th scope="col" className="hidden px-4 py-2 text-right md:table-cell">
                    開封率
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.scenarioPerformance.map((scenario) => (
                  <tr
                    key={scenario.key}
                    className="cursor-pointer border-b border-line transition-colors last:border-b-0 hover:bg-surface-hover"
                    // 行クリックで該当明細にドリルダウンできる（原則 11-1）
                    onClick={() =>
                      void navigate({ to: "/scenarios", search: { q: scenario.label } })
                    }
                  >
                    <td className="max-w-0 truncate px-4 py-2 font-medium text-fg">
                      {scenario.label}
                    </td>
                    <td className="num px-4 py-2 text-right font-semibold text-fg">
                      {formatCurrency(scenario.revenue)}
                    </td>
                    <td className="num hidden px-4 py-2 text-right text-fg-muted sm:table-cell">
                      {formatPercent(scenario.share)}
                    </td>
                    <td
                      className={cn(
                        "num px-4 py-2 text-right",
                        scenario.deltaPercent > 0
                          ? "text-success-fg"
                          : scenario.deltaPercent < 0
                            ? "text-critical-fg"
                            : "text-fg-muted"
                      )}
                    >
                      {formatDelta(scenario.deltaPercent)}
                    </td>
                    <td className="num hidden px-4 py-2 text-right text-fg-muted md:table-cell">
                      {formatPercent(scenario.openRate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel
            title="流入元別セッション"
            description={`AI検索経由が全体の ${formatPercent(report.aiSessionShare)}（AIO の観測点）`}
          >
            <BarList items={report.sourceSessions} formatValue={formatCompact} />
          </Panel>
        </PageBody>
      )}
    </>
  );
}
